import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Users, Plus, Minus, X, Loader2, AlertCircle, CheckCircle, ShoppingCart } from 'lucide-react';
import subscriptionService from '../../services/subscription';
import type {
  UserLicense,
  AvailableUser,
  OrganisationSubscription,
  SubscriptionPlan,
} from '../../types/subscription';
import { useAuth } from '../../context/AuthContext';
import { useOrgTimezone, formatDateInTz } from '@/utils/timezone';

/** One seat tier as the page shows it: the plan's type merged with what the org holds. */
interface Tier {
  type_key: string;
  name: string;
  description?: string | null;
  price_per_seat: number;
  total_seats: number;
  assigned_seats: number;
}

// The axios response interceptor flattens errors into { message, status,
// errors } before rejecting, so real server messages live on err.message.
const errorMessage = (err: any, fallback: string) =>
  err?.message || err?.response?.data?.error?.message || fallback;

const fmt = (n: number) => subscriptionService.formatCurrency(n);

/**
 * Seats & users for one subscription — the ONE place an org admin adds seats.
 *
 * It used to be one of three: a cart page, an "Adjust Seat Counts" button
 * here that only bounced back to the subscriptions list, and a reactivate
 * modal. Now assigning is the entry point and buying is part of it: pick
 * more people than there are free seats and the difference is bought in the
 * same action. "Buy seats" remains for buying ahead of hiring.
 *
 * Tiers come from the PLAN, not just the seats the org holds, so a tier
 * nobody has bought yet still shows (at 0/0) and its first seat can be bought
 * here instead of in a separate flow.
 */
function LicenseManagement() {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgTz = useOrgTimezone();

  const [subscription, setSubscription] = useState<OrganisationSubscription | null>(null);
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [licenses, setLicenses] = useState<UserLicense[]>([]);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [selectedTypeKey, setSelectedTypeKey] = useState<string | null>(null);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const tiers: Tier[] = (() => {
    if (!subscription) return [];
    const held = new Map((subscription.seats ?? []).map(s => [s.type_key, s]));
    const fromPlan = (plan?.license_types ?? []).map(lt => {
      const seat = held.get(lt.type_key);
      held.delete(lt.type_key);
      return {
        type_key: lt.type_key,
        name: lt.name,
        description: lt.description,
        price_per_seat: Number(lt.price_per_seat) || 0,
        total_seats: seat?.total_seats ?? 0,
        assigned_seats: seat?.assigned_seats ?? 0,
      };
    });
    // Seats on a type the plan no longer lists still hold users — keep them visible.
    const orphans = [...held.values()].map(s => ({
      type_key: s.type_key,
      name: s.name,
      price_per_seat: Number(s.price_per_seat) || 0,
      total_seats: s.total_seats,
      assigned_seats: s.assigned_seats,
    }));
    return [...fromPlan, ...orphans];
  })();

  const selectedTier = tiers.find(t => t.type_key === selectedTypeKey);

  const loadTypeData = useCallback(async (sub: OrganisationSubscription, typeKey: string) => {
    const [licensesResponse, usersResponse] = await Promise.all([
      subscriptionService.getLicenseAssignments(sub.module_key),
      subscriptionService.getAvailableUsers(sub.module_key!, typeKey),
    ]);
    if (licensesResponse.success) setLicenses(licensesResponse.data);
    if (usersResponse.success) setAvailableUsers(usersResponse.data);
  }, []);

  /** Reload everything, staying on `keepType` when given. */
  const loadData = useCallback(async (keepType?: string | null) => {
    setError(null);
    try {
      const [subsResponse, plansResponse] = await Promise.all([
        subscriptionService.getOrganisationSubscriptions(),
        subscriptionService.getAvailablePlans(),
      ]);
      if (!subsResponse.success) return;
      const sub = subsResponse.data.find(s => s.id === parseInt(subscriptionId!));
      if (!sub) {
        navigate('/subscriptions', { replace: true });
        return;
      }
      setSubscription(sub);
      const subPlan = plansResponse.success
        ? plansResponse.data.find(p => p.id === sub.plan_id) ?? null
        : null;
      setPlan(subPlan);

      // Default tier: first one the org holds seats on, else the plan's first.
      const initialType = keepType
        ?? sub.seats?.find(s => s.total_seats > 0)?.type_key
        ?? subPlan?.license_types?.[0]?.type_key
        ?? sub.seats?.[0]?.type_key
        ?? null;
      setSelectedTypeKey(initialType);
      if (initialType) await loadTypeData(sub, initialType);
    } catch (err: any) {
      if (err?.status === 403) {
        navigate('/dashboard', { replace: true });
        return;
      }
      setError('Failed to load seat information');
    } finally {
      setLoading(false);
    }
  }, [subscriptionId, navigate, loadTypeData]);

  useEffect(() => {
    loadData();
  }, [loadData, user?.organisation_id]);

  const selectTier = (typeKey: string) => {
    setSelectedTypeKey(typeKey);
    setNotice(null);
    if (subscription) loadTypeData(subscription, typeKey);
  };

  const handleRevokeLicense = async (license: UserLicense) => {
    if (!subscription) return;
    const name = license.username || `user #${license.user_id}`;
    if (!confirm(`Remove ${name} from their ${license.license_type_name || license.type_key} seat? The seat stays yours to give to someone else.`)) {
      return;
    }
    setActionLoading(true);
    try {
      const response = await subscriptionService.revokeLicense({
        user_id: license.user_id,
        module_key: subscription.module_key!,
        type_key: license.type_key!,
      });
      if (response.success) await loadData(selectedTypeKey);
      else setError((response as any)?.error?.message || 'Failed to remove seat');
    } catch (err: any) {
      setError(errorMessage(err, 'Failed to remove seat'));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-error-50 border border-error-200 rounded-xl p-6 text-center">
            <AlertCircle className="h-10 w-10 text-error-600 mx-auto mb-4" />
            <p className="text-error-700 mb-4">{error || 'Subscription not found'}</p>
            <Button variant="outline" onClick={() => navigate('/subscriptions')}>
              Back to Subscriptions
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const availableSeats = selectedTier
    ? Math.max(0, selectedTier.total_seats - selectedTier.assigned_seats)
    : 0;
  const licensesForTier = licenses.filter(l => l.type_key === selectedTypeKey);
  const canBuy = subscription.status === 'active';
  const canAssign = !!selectedTier && availableUsers.length > 0 && (availableSeats > 0 || canBuy);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Seats & Users"
        subtitle={`Who can use ${subscription.name}`}
        icon={<Users className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-xl p-4 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-error-600 flex-shrink-0" />
            <p className="text-error-700 min-w-0 break-words">{error}</p>
          </div>
        )}
        {notice && (
          <div className="mb-6 bg-success-50 border border-success-200 rounded-xl p-4 flex items-start gap-2">
            <CheckCircle className="h-5 w-5 text-success-600 flex-shrink-0" />
            <p className="text-success-700 min-w-0 break-words">{notice}</p>
          </div>
        )}

        {/* Seat tier tabs */}
        {tiers.length > 1 && (
          <div className="mb-6 flex flex-wrap gap-2">
            {tiers.map(tier => (
              <button
                key={tier.type_key}
                onClick={() => selectTier(tier.type_key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  selectedTypeKey === tier.type_key
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-neutral-700 border-neutral-200 hover:border-primary-300'
                }`}
              >
                {tier.name}
                <span className={`ml-2 text-xs ${selectedTypeKey === tier.type_key ? 'text-primary-100' : 'text-neutral-500'}`}>
                  {tier.assigned_seats}/{tier.total_seats}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Seat summary for selected tier */}
        {selectedTier && (
          <div className="mb-8 bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="min-w-0">
                <p className="text-lg font-semibold text-neutral-900">{selectedTier.name}</p>
                {selectedTier.description && (
                  <p className="text-sm text-neutral-500 mt-0.5">{selectedTier.description}</p>
                )}
                <p className="text-sm text-neutral-500 mt-1">{fmt(selectedTier.price_per_seat)} per seat / month</p>
              </div>
              {canBuy && (
                <Button variant="outline" size="sm" onClick={() => setShowBuyModal(true)}>
                  <ShoppingCart className="h-4 w-4" />
                  Buy seats
                </Button>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4 mt-5 pt-5 border-t border-neutral-100">
              <div>
                <p className="text-sm text-neutral-500 mb-1">Seats</p>
                <p className="text-lg font-semibold text-neutral-900">{selectedTier.total_seats}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-500 mb-1">In use</p>
                <p className="text-lg font-semibold text-neutral-900">{selectedTier.assigned_seats}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-500 mb-1">Free</p>
                <p className={`text-lg font-semibold ${availableSeats > 0 ? 'text-success-600' : 'text-neutral-900'}`}>
                  {availableSeats}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold text-neutral-900">
            Users ({licensesForTier.length})
          </h2>
          <Button
            variant="primary"
            onClick={() => { setNotice(null); setShowAssignModal(true); }}
            disabled={!canAssign}
          >
            <Plus className="h-4 w-4" />
            Add users
          </Button>
        </div>

        {/* Licenses table */}
        {licensesForTier.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-12 text-center">
            <Users className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Nobody has this seat yet</h3>
            <p className="text-neutral-600 mb-6">
              {availableUsers.length === 0
                ? 'Invite people to your organisation first, then give them a seat here.'
                : `Add users to give them access to ${selectedTier?.name ?? 'this tier'}.`}
            </p>
            <Button
              variant="primary"
              onClick={() => { setNotice(null); setShowAssignModal(true); }}
              disabled={!canAssign}
            >
              <Plus className="h-4 w-4" />
              Add users
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Since</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Given by</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {licensesForTier.map((license) => (
                    <tr key={license.id} className="hover:bg-neutral-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold">
                            {license.username?.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-3">
                            <p className="font-medium text-neutral-900">{license.username}</p>
                            {license.first_name && (
                              <p className="text-sm text-neutral-500">
                                {license.first_name} {license.last_name}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                        {license.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                        {formatDateInTz(license.assigned_at, orgTz)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                        {license.assigned_by_username || 'System'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevokeLicense(license)}
                          disabled={actionLoading}
                        >
                          <X className="h-4 w-4 text-error-600" />
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showAssignModal && selectedTier && (
        <AssignUsersModal
          subscription={subscription}
          tier={selectedTier}
          availableSeats={availableSeats}
          canBuy={canBuy}
          users={availableUsers}
          onClose={() => setShowAssignModal(false)}
          onDone={async (message) => {
            setShowAssignModal(false);
            setNotice(message);
            await loadData(selectedTypeKey);
          }}
          onPartial={async (message) => {
            setShowAssignModal(false);
            setError(message);
            await loadData(selectedTypeKey);
          }}
        />
      )}

      {showBuyModal && selectedTier && (
        <BuySeatsModal
          subscription={subscription}
          tier={selectedTier}
          onClose={() => setShowBuyModal(false)}
          onDone={async (message) => {
            setShowBuyModal(false);
            setNotice(message);
            await loadData(selectedTypeKey);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Modals
// ============================================================================

function ModalShell({ title, onClose, children, footer }: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[85vh] flex flex-col overflow-hidden">
        <div className="p-6 border-b border-neutral-200 flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-neutral-900 min-w-0">{title}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">{children}</div>
        <div className="p-6 border-t border-neutral-200 flex gap-3">{footer}</div>
      </div>
    </div>
  );
}

/** "$X/month added to your subscription" block shared by both modals. */
function CostLine({ count, tier }: { count: number; tier: Tier }) {
  return (
    <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 text-sm">
      <div className="flex justify-between gap-3 text-neutral-700">
        <span>{count} × {tier.name} seat{count === 1 ? '' : 's'}</span>
        <span className="font-semibold text-neutral-900 whitespace-nowrap">+{fmt(count * tier.price_per_seat)}/month</span>
      </div>
      <p className="text-xs text-neutral-500 mt-2">
        Added to your subscription straight away. We'll email you an invoice.
      </p>
    </div>
  );
}

function PoField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label htmlFor="seat-po" className="block text-sm font-medium text-neutral-700 mb-1">
        Your PO number (optional)
      </label>
      <input
        id="seat-po"
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Shown on the invoice"
        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
    </div>
  );
}

function AssignUsersModal({ subscription, tier, availableSeats, canBuy, users, onClose, onDone, onPartial }: {
  subscription: OrganisationSubscription;
  tier: Tier;
  availableSeats: number;
  canBuy: boolean;
  users: AvailableUser[];
  onClose: () => void;
  onDone: (message: string) => void;
  onPartial: (message: string) => void;
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [po, setPo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shortfall = Math.max(0, selected.size - availableSeats);
  const blocked = shortfall > 0 && !canBuy;

  const toggle = (id: number) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async () => {
    setSubmitting(true);
    setError(null);

    // Buy first: an assignment against a seat that does not exist yet is
    // refused. If the purchase fails nothing has changed and the modal stays
    // open to say why.
    if (shortfall > 0) {
      try {
        const res = await subscriptionService.addSeats(subscription, tier.type_key, shortfall, po.trim());
        if (!res.success) throw new Error('Purchase failed');
      } catch (err: any) {
        setError(errorMessage(err, 'Could not buy the extra seats'));
        setSubmitting(false);
        return;
      }
    }

    // Seats now exist. Assign one by one; a failure part-way leaves the seats
    // bought and free, which the page then shows — say so rather than
    // pretending the whole action failed.
    const failed: string[] = [];
    for (const id of selected) {
      try {
        const res = await subscriptionService.assignLicense({
          user_id: id,
          module_key: subscription.module_key!,
          type_key: tier.type_key,
        });
        if (!res.success) throw new Error();
      } catch {
        failed.push(users.find(u => u.id === id)?.username ?? `user #${id}`);
      }
    }
    setSubmitting(false);

    const assigned = selected.size - failed.length;
    const bought = shortfall > 0 ? `Bought ${shortfall} seat${shortfall === 1 ? '' : 's'}. ` : '';
    if (failed.length === 0) {
      onDone(`${bought}${assigned} user${assigned === 1 ? '' : 's'} can now use ${tier.name}.`);
    } else {
      onPartial(`${bought}Could not give a seat to ${failed.join(', ')} — the seat is still free to assign.`);
    }
  };

  const confirmLabel = shortfall > 0
    ? `Buy ${shortfall} seat${shortfall === 1 ? '' : 's'} & add`
    : selected.size === 0 ? 'Add users' : `Add ${selected.size} user${selected.size === 1 ? '' : 's'}`;

  return (
    <ModalShell
      title={`Add users to ${tier.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="primary"
            className="flex-1"
            onClick={submit}
            disabled={selected.size === 0 || submitting || blocked}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm text-neutral-600 mb-4">
        {availableSeats > 0
          ? `${availableSeats} free seat${availableSeats === 1 ? '' : 's'}.${canBuy ? ' Pick more people than that and the extra seats are bought for you.' : ''}`
          : canBuy
            ? 'No free seats — a seat is bought for each person you add.'
            : 'No free seats.'}
      </p>

      {error && (
        <div className="mb-4 bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span className="min-w-0 break-words">{error}</span>
        </div>
      )}

      <div className="space-y-2">
        {users.map((u) => (
          <label
            key={u.id}
            className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-colors ${
              selected.has(u.id) ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 hover:border-neutral-300'
            }`}
          >
            <input
              type="checkbox"
              checked={selected.has(u.id)}
              onChange={() => toggle(u.id)}
              className="mr-3 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-neutral-900 break-words">{u.username}</p>
              <p className="text-sm text-neutral-500 break-all">{u.email}</p>
              {u.first_name && (
                <p className="text-xs text-neutral-400">{u.first_name} {u.last_name}</p>
              )}
            </div>
          </label>
        ))}
      </div>

      {shortfall > 0 && canBuy && (
        <div className="mt-4 space-y-3">
          <CostLine count={shortfall} tier={tier} />
          <PoField value={po} onChange={setPo} />
        </div>
      )}
      {blocked && (
        <div className="mt-4 bg-warning-50 border border-warning-200 rounded-lg p-3 text-sm text-warning-700">
          Only {availableSeats} seat{availableSeats === 1 ? ' is' : 's are'} free, and seats can't be bought while this subscription isn't active.
        </div>
      )}
    </ModalShell>
  );
}

function BuySeatsModal({ subscription, tier, onClose, onDone }: {
  subscription: OrganisationSubscription;
  tier: Tier;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [count, setCount] = useState(1);
  const [po, setPo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await subscriptionService.addSeats(subscription, tier.type_key, count, po.trim());
      if (!res.success) throw new Error('Purchase failed');
      onDone(`Bought ${count} ${tier.name} seat${count === 1 ? '' : 's'}. Add users to put ${count === 1 ? 'it' : 'them'} to work.`);
    } catch (err: any) {
      setError(errorMessage(err, 'Could not buy seats'));
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title={`Buy ${tier.name} seats`}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" className="flex-1" onClick={submit} disabled={submitting || count < 1}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
            Purchase
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-4 bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span className="min-w-0 break-words">{error}</span>
        </div>
      )}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <label htmlFor="seat-count" className="text-sm font-medium text-neutral-700">How many?</label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setCount(c => Math.max(1, c - 1))}
              className="p-2 rounded bg-neutral-100 hover:bg-neutral-200 text-neutral-600"
              aria-label="One fewer"
            >
              <Minus className="h-4 w-4" />
            </button>
            <input
              id="seat-count"
              type="number"
              min={1}
              value={count}
              onChange={e => setCount(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-16 text-center border border-neutral-300 rounded px-1 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={() => setCount(c => c + 1)}
              className="p-2 rounded bg-neutral-100 hover:bg-neutral-200 text-neutral-600"
              aria-label="One more"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
        <CostLine count={count} tier={tier} />
        <PoField value={po} onChange={setPo} />
      </div>
    </ModalShell>
  );
}

export default LicenseManagement;
